import type { ChatTurn, MapModel, TurnError, WidgetConfig } from './model';
import { emptyModel } from './model';
import { DEFAULTS } from './defaults';
import { OpenWebUIClient, ServiceError } from './openwebui';
import { extractMap, hasContent } from './extract';
import { askingAgainHelps, ChatView, parseExamples } from './chat';
import { AnswerMap } from './map';
import { parseGestures } from './gestures';
import { MapToggle, parsePreset, rememberChoice, rememberedChoice, resolveMapShown, type VisitorChoice } from './map-toggle';
import { DetailsSheet } from './sheet';
import styles from './styles.css?inline';
import fonts from './fonts.css?inline';
import maplibreCss from 'maplibre-gl/dist/maplibre-gl.css?inline';

const STORAGE_KEY = 'nds-chat-map:turns';
const MAP_PANE_ID = 'nds-map';
const MAP_NEWS_ID = 'nds-map-news';
/** the map's line while it has nothing to show: before the first answer with places, and after one without */
const HINT_ASK = 'Stell eine Frage – Orte und Wege aus der Antwort erscheinen hier.';
const HINT_CHOOSE = 'Wähle im Verlauf eine Antwort mit Orten, um sie hier zu sehen.';
const uid = (): string => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

export class NdsChatMap extends HTMLElement {
  private cfg: WidgetConfig = { ...DEFAULTS };
  private turns: ChatTurn[] = [];
  private client!: OpenWebUIClient;
  private chat!: ChatView;
  private map!: AnswerMap;
  private selectedId: string | null = null;
  private layout!: HTMLElement;
  private mapPane!: HTMLElement;
  private mapHint!: HTMLElement;
  private toggle!: MapToggle;
  private mapShown = true;
  /** the visitor's choice: pressed on this page view, or remembered from an earlier one (resolveMapShown) */
  private choice: VisitorChoice = { now: null, remembered: null };
  /** the selection changed while the map was hidden — draw it when the map comes back */
  private mapStale = false;
  /** the map pane's size when it was hidden; a different size on the way back means framing anew */
  private hiddenPaneSize = '';
  private readonly paneObserver = new ResizeObserver(() => {
    if (this.mapShown) this.map.resize();
  });
  // `map="auto"` follows the widget's width (a phone turned sideways, a resized column)
  private readonly hostObserver = new ResizeObserver(() => this.applyMapState());
  /** on a phone the on-screen keyboard shrinks the visual viewport; keep the input in sight */
  private readonly onViewportResize = (): void => this.chat.keepInputInView();

  static get observedAttributes(): string[] {
    return ['api-key', 'base-url', 'model', 'tool-ids', 'tiles-url', 'extended-hint', 'delete-chats', 'title', 'placeholder', 'ai-notice', 'ai-notice-short', 'ai-notice-href', 'examples', 'map', 'map-toggle', 'gestures'];
  }

  connectedCallback(): void {
    this.readConfig();
    if (!this.shadowRoot) this.mount();
    window.visualViewport?.addEventListener('resize', this.onViewportResize);
    this.paneObserver.observe(this.mapPane);
    this.hostObserver.observe(this);
  }

  disconnectedCallback(): void {
    this.map.returnFinger();
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
    this.paneObserver.disconnect();
    this.hostObserver.disconnect();
  }

  private mount(): void {
    addFontFaces(this.ownerDocument);
    const root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = maplibreCss + '\n' + styles;
    root.appendChild(style);

    const layout = (this.layout = document.createElement('div'));
    layout.className = 'layout';
    root.appendChild(layout);

    this.client = new OpenWebUIClient(() => this.cfg);
    this.chat = new ChatView({ onSend: (t) => void this.ask(t), onSelect: (id, showMap) => this.select(id, showMap), onRetry: (id) => this.retry(id) }, () => this.cfg);
    layout.appendChild(this.chat.root);
    // beside the chat, not in it: the sheet of a place makes the chat inert, the announcements go on
    layout.appendChild(this.chat.status);
    this.toggle = new MapToggle(MAP_PANE_ID, MAP_NEWS_ID, () => this.setMapShown(!this.mapShown, true));
    this.chat.addHeaderAction(this.toggle.button, this.toggle.status);

    this.mapPane = document.createElement('section');
    this.mapPane.className = 'map-pane';
    this.mapPane.id = MAP_PANE_ID;
    this.mapPane.setAttribute('aria-label', 'Karte');
    this.mapPane.innerHTML = '<div class="map"></div><p class="map-hint"></p>';
    this.mapHint = this.mapPane.querySelector('.map-hint')!;
    layout.appendChild(this.mapPane);
    const sheet = new DetailsSheet(layout, () => [this.chat.root, this.mapPane]);
    this.map = new AnswerMap(this.mapPane.querySelector('.map')!, this.cfg.tilesUrl, this.cfg.home, sheet);
    // before the stored conversation is drawn: a map hidden from the start draws once it is shown, at its real size
    this.choice = { now: null, remembered: rememberedChoice() };
    this.applyMapState();
    this.map.setGestures(this.cfg.gestures);

    this.restore();
    this.chat.render(this.turns);
    const last = [...this.turns].reverse().find((t) => t.role === 'assistant' && hasContent(t.map));
    if (last) this.select(last.id);
    else this.showEmptyState(emptyModel());
    if (!this.cfg.apiKey) this.pushError({ kind: 'setup', detail: 'no api-key attribute on <nds-chat-map>' });
  }

  attributeChangedCallback(name: string, before: string | null, value: string | null): void {
    this.readConfig();
    if (!this.chat) return;
    // a key that arrives after mount clears the "no key" notice
    if (this.cfg.apiKey) {
      const count = this.turns.length;
      this.turns = this.turns.filter((t) => t.error?.kind !== 'setup');
      if (this.turns.length !== count) this.chat.render(this.turns);
    }
    // the host page says anew how the map should be: that counts over the choice of this page view
    if (name === 'map' && before !== value) this.choice = { ...this.choice, now: null };
    // embed.html sets its texts from the URL after the element is in the page; a key brings the examples
    if (name === 'title' || name === 'placeholder' || name === 'examples' || name === 'api-key' || name.startsWith('ai-notice')) this.chat.refreshTexts();
    this.applyMapState();
    this.map.setGestures(this.cfg.gestures);
  }

  private readConfig(): void {
    const g = (n: string): string | null => this.getAttribute(n);
    const apiKey = g('api-key') ?? DEFAULTS.apiKey;
    this.cfg = {
      ...DEFAULTS,
      baseUrl: g('base-url') ?? DEFAULTS.baseUrl,
      apiKey,
      model: g('model') ?? DEFAULTS.model,
      toolIds: g('tool-ids') ? g('tool-ids')!.split(',').map((s) => s.trim()).filter(Boolean) : DEFAULTS.toolIds,
      tilesUrl: g('tiles-url') ?? DEFAULTS.tilesUrl,
      extendedHint: g('extended-hint') !== 'false',
      deleteChats: g('delete-chats') === 'true',
      title: g('title') ?? DEFAULTS.title,
      placeholder: g('placeholder') ?? DEFAULTS.placeholder,
      aiNotice: g('ai-notice') ?? DEFAULTS.aiNotice,
      // a host's own notice stands for both widths unless it brings its own (non-empty) short form
      aiNoticeShort: g('ai-notice-short') || (g('ai-notice') ?? DEFAULTS.aiNoticeShort),
      aiNoticeHref: g('ai-notice-href') ?? DEFAULTS.aiNoticeHref,
      // without a key no question can go out: nothing to tap
      examples: apiKey ? parseExamples(g('examples'), DEFAULTS.examples) : [],
      placeholderShort: g('placeholder') ?? DEFAULTS.placeholderShort,
      map: parsePreset(g('map'), DEFAULTS.map),
      mapToggle: g('map-toggle') !== 'false',
      gestures: parseGestures(g('gestures'), DEFAULTS.gestures),
    };
  }

  private async ask(text: string): Promise<void> {
    if (!this.cfg.apiKey) return;
    // a new question: an older failed one is no longer offered again
    for (const t of this.turns) if (t.error?.question) t.error = { kind: t.error.kind, detail: t.error.detail };
    const user: ChatTurn = { id: uid(), role: 'user', text, createdAt: Date.now() };
    const assistant: ChatTurn = { id: uid(), role: 'assistant', text: '', createdAt: Date.now(), answer: { text: '', calls: [], results: [], running: [], done: false } };
    this.turns.push(user, assistant);
    this.chat.render(this.turns);
    this.chat.setBusy(true);
    try {
      const history = this.turns
        .filter((t) => t.id !== user.id && t.id !== assistant.id && !t.error && (t.role === 'user' || t.answer?.done))
        .slice(-8)
        .map((t) => ({ role: t.role, content: t.role === 'user' ? t.text : (t.answer?.text ?? '') }));
      const handle = await this.client.ask(history, text);
      const answer = await this.client.waitFor(handle, (partial) => {
        assistant.answer = partial;
        this.chat.render(this.turns);
      });
      assistant.answer = answer;
      assistant.text = answer.text;
      assistant.map = extractMap(answer.results, answer.text);
      this.persist();
      this.chat.render(this.turns);
      this.select(assistant.id);
      this.chat.answered(assistant.id);
      // the map does not open by itself; the toggle says whether the new answer has something to see
      if (!this.mapShown) this.toggle.setFresh(this.cfg.mapToggle && hasContent(assistant.map));
      if (this.cfg.deleteChats) void this.client.delete(handle);
    } catch (e) {
      assistant.error = turnError(e, text);
      console.warn(`nds-chat-map: ${assistant.error.detail}`);
      this.chat.render(this.turns);
      this.chat.answered(assistant.id);
      this.chat.offerAgain(text);
    } finally {
      this.chat.setBusy(false);
    }
  }

  /** "Nochmal fragen": the failed question and its error make way, the question is asked again. */
  private retry(id: string): void {
    const at = this.turns.findIndex((t) => t.id === id);
    const question = this.turns[at]?.error?.question;
    if (!question) return;
    const from = this.turns[at - 1]?.role === 'user' ? at - 1 : at;
    this.turns.splice(from, at - from + 1);
    void this.ask(question);
  }

  /** Make an answer the one on the map. `showMap`: the visitor asked to see it there (the answer's
   *  map badge), which brings a hidden map back; a click elsewhere on the answer only selects it. */
  private select(id: string, showMap = false): void {
    const turn = this.turns.find((t) => t.id === id);
    if (!turn || turn.role !== 'assistant') return;
    this.selectedId = id;
    this.chat.setSelected(id);
    this.drawSelection();
    if (showMap && !this.mapShown && this.cfg.mapToggle) this.setMapShown(true, true);
  }

  /** The selected answer on the map — or, while the map is hidden, once it comes back. */
  private drawSelection(): void {
    const model: MapModel = this.turns.find((t) => t.id === this.selectedId)?.map ?? emptyModel();
    this.showEmptyState(model);
    if (!this.mapShown) {
      this.mapStale = true;
      return;
    }
    this.mapStale = false;
    this.map.show(model);
  }

  /** While the map has nothing to show for `model`, its hint says what to do about it. */
  private showEmptyState(model: MapModel): void {
    this.mapPane.classList.toggle('is-empty', !hasContent(model));
    this.mapHint.textContent = this.turns.some((t) => hasContent(t.map)) ? HINT_CHOOSE : HINT_ASK;
  }

  /** Show the map as the host preset, the visitor's choice and the widget's width say. */
  private applyMapState(): void {
    this.toggle.setAvailable(this.cfg.mapToggle);
    if (!this.cfg.mapToggle) this.toggle.setFresh(false);
    this.setMapShown(resolveMapShown(this.cfg.map, this.cfg.mapToggle, this.choice, this.contentWidth()));
  }

  /** The width the stacking break in styles.css measures: the host's content box. */
  private contentWidth(): number {
    const style = getComputedStyle(this);
    return this.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
  }

  private setMapShown(shown: boolean, byUser = false): void {
    if (byUser) {
      // remembered as well: should the host switch to `auto` later, this choice is the one to keep
      this.choice = { now: shown, remembered: shown };
      rememberChoice(shown);
    }
    this.toggle.setExpanded(shown);
    this.chat.describeMap(shown, this.cfg.mapToggle);
    if (shown === this.mapShown) return;
    this.mapShown = shown;
    if (!shown) this.hiddenPaneSize = paneSize(this.mapPane);
    this.layout.classList.toggle('is-map-hidden', !shown);
    if (!shown) return;
    this.toggle.setFresh(false);
    // the pane had no size while hidden: size the canvas first, then frame the answer in it — if it
    // changed meanwhile or comes back in another shape (an open popup stays when nothing changed)
    this.map.resize();
    if (this.mapStale || paneSize(this.mapPane) !== this.hiddenPaneSize) this.drawSelection();
  }

  private pushError(error: TurnError): void {
    console.warn(`nds-chat-map: ${error.detail}`);
    this.turns.push({ id: uid(), role: 'assistant', text: '', createdAt: Date.now(), error });
    this.chat.render(this.turns);
  }

  private persist(): void {
    try {
      const slim = this.turns.filter((t) => !t.error && (t.role === 'user' || t.answer?.done)).slice(-40);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
    } catch {
      /* storage may be unavailable */
    }
  }

  private restore(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) this.turns = JSON.parse(raw) as ChatTurn[];
      // a conversation saved by an older bundle may lack fields added since (tracks, primaryJourney)
      for (const t of this.turns) if (t.map) t.map = { ...emptyModel(), ...t.map };
    } catch {
      this.turns = [];
    }
  }

  /** Programmatic API for host pages. */
  public askQuestion(text: string): void {
    void this.ask(text);
  }

  public clear(): void {
    this.turns = [];
    this.selectedId = null;
    this.persist();
    this.chat.render(this.turns);
    this.toggle.setFresh(false);
    this.drawSelection();
  }
}

const paneSize = (pane: HTMLElement): string => `${pane.clientWidth}x${pane.clientHeight}`;

/** A failed question as the chat shows it: the kind decides the visitor's sentence, the detail goes to
 *  the console and `data-detail`, the question is offered again where that can help. */
function turnError(e: unknown, question: string): TurnError {
  const { kind, detail } = e instanceof ServiceError
    ? e
    : { kind: 'unexpected' as const, detail: e instanceof Error ? `${e.name}: ${e.message}` : String(e) };
  return askingAgainHelps(kind) ? { kind, detail, question } : { kind, detail };
}

/** Chrome ignores @font-face inside a shadow root: the font rules go to the page's document, once. */
function addFontFaces(doc: Document): void {
  if (doc.head.querySelector('style[data-nds-chat-map-fonts]')) return;
  const style = doc.createElement('style');
  style.dataset.ndsChatMapFonts = '';
  style.textContent = fonts;
  doc.head.appendChild(style);
}

export function register(tag = 'nds-chat-map'): void {
  if (!customElements.get(tag)) customElements.define(tag, NdsChatMap);
}
